import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import styled from 'styled-components'

import { Button, InviteIcon } from '@blade/ui-kit'
import { referralLinkAtom } from '@root/entities/referral'
import { CreateCurrencyFormDrawer } from '@root/features/create-currency/ui/currency-create-form-drawer'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import {
  coinsDrawerAtom,
  createCurrencyDrawerAtom,
  inviteDrawerAtom,
  leaderboardTabAtom,
  profileTabAtom,
} from '@root/features/user/profile-header/atoms'
import { CoinsDrawer } from '@root/widgets/coins'
import { LeaderboardDrawer } from '@root/widgets/leaderboard'
import { InviteReferralDrawer } from '@root/widgets/referral'

import { ProfileNavigation } from './profile-navigation'
import { ProfileScreenCurrent } from './profile-screen-current'
import { ProfileScreenOther } from './profile-screen-other'

import type { DelugeId } from '@root/entities/user-short'

type ProfileScreenProps = {}

export const ProfileScreen: React.FC<ProfileScreenProps> = () => {
  const { t } = useTranslation()
  const routeParams = useParams<{ delugeId?: DelugeId }>()
  const delugeId = routeParams.delugeId

  const [tab, setProfileTab] = useAtom(profileTabAtom)
  const [leaderboardTab, setLeaderboardTab] = useAtom(leaderboardTabAtom)

  const [referralLink] = useAtom(referralLinkAtom)
  const [showInviteDrawer, setShowInviteDrawer] = useAtom(inviteDrawerAtom)
  const [showCoinsDrawer, setShowCoinsDrawer] = useAtom(coinsDrawerAtom)
  const [showCreateCurrencyDrawer, setShowCreateCurrencyDrawer] = useAtom(
    createCurrencyDrawerAtom,
  )

  const handleInviteClick = () => {
    if (referralLink) {
      setShowInviteDrawer(true)
    } else {
      setLeaderboardTab(undefined)
      setProfileTab('referral')
    }
  }

  const renderContent = () => {
    if (delugeId) {
      return <ProfileScreenOther delugeId={delugeId} tab={tab} />
    }
    return <ProfileScreenCurrent tab={tab} />
  }

  return (
    <Root>
      <ProfileNavigation
        profileOwner={
          delugeId ? ProfileOwner.AnotherUsers : ProfileOwner.CurrentUser
        }
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={delugeId ?? `current-${tab}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>
      <LeaderboardDrawer
        initialTab={leaderboardTab}
        open={!!leaderboardTab}
        onOpenChange={() => setLeaderboardTab(undefined)}
        renderFooter={(tab) =>
          tab === 'referral' ? (
            <Button
              iconLeft={<InviteIcon />}
              onClick={handleInviteClick}
              variant="solid"
              color="primary"
              radius="rounded"
              fullWidth
              type="button"
            >
              {t('referral.invite_referral')}
            </Button>
          ) : undefined
        }
      />
      <CoinsDrawer
        open={showCoinsDrawer}
        onOpenChange={setShowCoinsDrawer}
        renderFooter={() => (
          <Button
            type="button"
            radius="rounded"
            variant="solid"
            size="s"
            fullWidth
            color="primary"
            onClick={() => setShowCreateCurrencyDrawer(true)}
          >
            {t('common.create_coin')}
          </Button>
        )}
      />
      <InviteReferralDrawer
        open={showInviteDrawer}
        onOpenChange={setShowInviteDrawer}
      />
      <CreateCurrencyFormDrawer
        open={showCreateCurrencyDrawer}
        onOpenChange={setShowCreateCurrencyDrawer}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: var(--background-1);
  padding-block-start: 12px;
  height: auto;

  overflow: auto;
`
